import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import NewspaperRequestsPanel from './NewspaperRequestsPanel';
import { withAuthToken } from '../../utils/fileUrl';

const fullName = (u) =>
  u ? `${u.firstName} ${u.lastNamePaternal} ${u.lastNameMaternal}`.trim() : '';

function formatDateTime(value) {
  if (!value) return '-';
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function formatMetadata(metadata) {
  if (!metadata || typeof metadata !== 'object') return '-';
  try {
    const text = JSON.stringify(metadata);
    if (text.length <= 120) return text;
    return `${text.slice(0, 117)}...`;
  } catch (_error) {
    return '-';
  }
}

function formatFileSize(bytes) {
  const mb = Number(bytes || 0) / 1024 / 1024;
  return `${mb.toFixed(2)} MB`;
}

function userFilterLabel(u) {
  const full = `${String(u.firstName || '').trim()} ${String(u.lastNamePaternal || '').trim()} ${String(
    u.lastNameMaternal || ''
  ).trim()}`.replace(/\s+/g, ' ').trim();
  return full || 'Usuario';
}

function evidenceTitle(item) {
  const assemblyId = Number(item?.assemblyId || 0);
  if (assemblyId > 0) return `Asamblea #${assemblyId}`;
  return 'Evidencia sin asamblea';
}

function assemblyStatusLabel(status) {
  const map = {
    pending: 'Pendiente',
    in_progress: 'En progreso',
    completed: 'Completada',
    canceled: 'Cancelada'
  };
  return map[String(status || '').toLowerCase()] || status || '-';
}

function reportStatusLabel(status) {
  const map = {
    draft: 'Borrador',
    submitted: 'Enviado'
  };
  return map[String(status || '').toLowerCase()] || status || '-';
}

function toDateTimeFileStamp(value = new Date()) {
  const pad2 = (num) => String(num).padStart(2, '0');
  const year = value.getFullYear();
  const month = pad2(value.getMonth() + 1);
  const day = pad2(value.getDate());
  const hour = pad2(value.getHours());
  const minute = pad2(value.getMinutes());
  return `${year}${month}${day}-${hour}${minute}`;
}

function formatFilterDate(value) {
  const text = String(value || '').trim();
  if (!text) return 'Todos';
  const dt = new Date(text);
  if (Number.isNaN(dt.getTime())) return text;
  return dt.toLocaleString('es-MX', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function SummaryCard({ label, value, tone = 'default' }) {
  return (
    <article className={`admin-card admin-card-${tone}`}>
      <span className="admin-card-label">{label}</span>
      <strong className="admin-card-value">{value}</strong>
    </article>
  );
}

export default function AdminDashboard({
  toast,
  auth,
  adminCtx,
  activeModule = 'dashboard',
  onNavigateModule,
}) {
  const { user, logout } = auth;
  const {
    summary,
    eventsResult,
    usersResult,
    userOptions,
    assembliesResult,
    evidencesResult,
    loadingSummary,
    loadingEvents,
    loadingUsers,
    loadingAssemblies,
    loadingEvidences,
    loadingAssembliesByUser,
    filtersDraft,
    setFiltersDraft,
    usersFiltersDraft,
    setUsersFiltersDraft,
    assembliesFiltersDraft,
    setAssembliesFiltersDraft,
    assembliesByUserResult,
    evidencesFiltersDraft,
    setEvidencesFiltersDraft,
    eventsSort,
    applyFilters,
    applyUsersFilters,
    applyAssembliesFilters,
    applyEvidencesFilters,
    changePage,
    changeEventsPageSize,
    changeUsersPage,
    changeUsersPageSize,
    changeAssembliesPage,
    changeAssembliesPageSize,
    changeEvidencesPage,
    changeEvidencesPageSize,
    setEventsOrder,
    setUsersSort,
    setAssembliesSort,
    setEvidencesSort,
    reset
  } = adminCtx;

  const page = eventsResult?.page || 1;
  const totalPages = eventsResult?.totalPages || 1;
  const usersPage = usersResult?.page || 1;
  const usersTotalPages = usersResult?.totalPages || 1;
  const assembliesPage = assembliesResult?.page || 1;
  const assembliesTotalPages = assembliesResult?.totalPages || 1;
  const evidencesPage = evidencesResult?.page || 1;
  const evidencesTotalPages = evidencesResult?.totalPages || 1;
  const [adminSection, setAdminSection] = useState(
    activeModule === 'dashboard' ? 'users_actions' : 'assemblies_info'
  );
  const [selectedEvidence, setSelectedEvidence] = useState(null);

  const exportAssembliesByUser = () => {
    const rows = Array.isArray(assembliesByUserResult?.items) ? assembliesByUserResult.items : [];
    if (!rows.length) {
      window.alert('No hay datos para exportar con los filtros actuales.');
      return;
    }

    const dataRows = rows.map((item) => ({
      'Usuario': item.userFullName || '-',
      'Correo': item.userEmail || '-',
      'CEE / Entidad': item.isCee ? (item.ceeName || 'Sí') : 'No',
      'Total': Number(item.total || 0),
      'Pendiente': Number(item.pending || 0),
      'En progreso': Number(item.inProgress || 0),
      'Completada': Number(item.completed || 0),
      'Cancelada': Number(item.canceled || 0)
    }));

    const filtersRows = [
      { Filtro: 'Busqueda', Valor: String(assembliesFiltersDraft.search || '').trim() || 'Todos' },
      { Filtro: 'Desde', Valor: formatFilterDate(assembliesFiltersDraft.from) },
      { Filtro: 'Hasta', Valor: formatFilterDate(assembliesFiltersDraft.to) },
      { Filtro: 'Exportado', Valor: formatDateTime(new Date().toISOString()) }
    ];

    const workbook = XLSX.utils.book_new();
    const summarySheet = XLSX.utils.json_to_sheet(dataRows);
    const filtersSheet = XLSX.utils.json_to_sheet(filtersRows);

    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Asambleas por usuario');
    XLSX.utils.book_append_sheet(workbook, filtersSheet, 'Filtros');

    const fileName = `asambleas-por-usuario-${toDateTimeFileStamp()}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  const exportAssembliesDetail = () => {
    const rows = Array.isArray(assembliesResult?.items) ? assembliesResult.items : [];
    if (!rows.length) {
      window.alert('No hay asambleas en la tabla de detalle para exportar.');
      return;
    }

    const selectedUser = userOptions.find(
      (u) => String(u.id) === String(assembliesFiltersDraft.userId || '')
    );

    const dataRows = rows.map((item) => ({
      'ID': Number(item.id || 0),
      'Usuario': item.userFullName || '-',
      'Correo': item.userEmail || '-',
      'Fecha programada': item.scheduledDate || '-',
      'Hora inicio': item.startTime || '-',
      'Hora termino': item.endTime || '-',
      'Colonia': item.colonia || '-',
      'Codigo postal': item.postalCode || '-',
      'Direccion': item.address || '-',
      'Asistentes': item.attendeesCount != null ? Number(item.attendeesCount) : '-',
      'Estado asamblea': assemblyStatusLabel(item.status),
      'Estado reporte': item.reportStatus ? reportStatusLabel(item.reportStatus) : '-',
      'Reporte enviado': item.reportSubmittedAt ? formatDateTime(item.reportSubmittedAt) : '-'
    }));

    const filtersRows = [
      { Filtro: 'Busqueda', Valor: String(assembliesFiltersDraft.search || '').trim() || 'Todos' },
      {
        Filtro: 'Usuario',
        Valor: selectedUser ? userFilterLabel(selectedUser) : 'Todos'
      },
      {
        Filtro: 'Estado asamblea',
        Valor: assembliesFiltersDraft.status ? assemblyStatusLabel(assembliesFiltersDraft.status) : 'Todos'
      },
      {
        Filtro: 'Estado reporte',
        Valor: assembliesFiltersDraft.reportStatus ? reportStatusLabel(assembliesFiltersDraft.reportStatus) : 'Todos'
      },
      { Filtro: 'Desde', Valor: formatFilterDate(assembliesFiltersDraft.from) },
      { Filtro: 'Hasta', Valor: formatFilterDate(assembliesFiltersDraft.to) },
      {
        Filtro: 'Orden',
        Valor: `${String(assembliesFiltersDraft.sortBy || 'scheduledDate')}:${String(
          assembliesFiltersDraft.sortOrder || 'desc'
        )}`
      },
      { Filtro: 'Exportado', Valor: formatDateTime(new Date().toISOString()) }
    ];

    const workbook = XLSX.utils.book_new();
    const detailSheet = XLSX.utils.json_to_sheet(dataRows);
    const filtersSheet = XLSX.utils.json_to_sheet(filtersRows);

    XLSX.utils.book_append_sheet(workbook, detailSheet, 'Detalle asambleas');
    XLSX.utils.book_append_sheet(workbook, filtersSheet, 'Filtros');

    const fileName = `detalle-asambleas-${toDateTimeFileStamp()}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  };

  useEffect(() => {
    if (activeModule === 'dashboard') {
      setAdminSection('users_actions');
      return;
    }

    if (activeModule === 'assemblies') {
      setAdminSection('assemblies_info');
    }
  }, [activeModule]);

  return (
    <div className="app-shell">
      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}

      <header className="app-header">
        <span className="header-title">Panel de Administracion · Actividades</span>
        <div className="header-user">
          <div className="user-info">
            <strong>{fullName(user)}</strong>
            <span>Administrador · {user.state}, {user.municipality}</span>
          </div>
          <button className="btn-ghost" onClick={() => logout(reset)}>Cerrar sesion</button>
        </div>
      </header>

      <div className="admin-shell">
        <aside className="admin-sidebar">
          <h3 className="admin-sidebar-title">Modulos</h3>
          <button
            className={`admin-sidebar-link ${activeModule === 'dashboard' ? 'active' : ''}`}
            onClick={() => onNavigateModule && onNavigateModule('dashboard')}
          >
            Dashboard Admin
          </button>
          <button
            className={`admin-sidebar-link ${activeModule === 'assemblies' ? 'active' : ''}`}
            onClick={() => onNavigateModule && onNavigateModule('assemblies')}
          >
            Manager de Asambleas
          </button>
          <button
            className={`admin-sidebar-link ${activeModule === 'newspaper' ? 'active' : ''}`}
            onClick={() => onNavigateModule && onNavigateModule('newspaper')}
          >
            Solicitudes Periodico
          </button>
        </aside>

        <div className="admin-page">
        {(activeModule === 'dashboard' || activeModule === 'assemblies') && (
          <>
        <section className="admin-toolbar">
          <div className="admin-filter-grid">
            <label className="form-field">
              <span className="field-label">Desde</span>
              <input
                type="datetime-local"
                value={filtersDraft.from}
                onChange={(e) => setFiltersDraft((prev) => ({ ...prev, from: e.target.value }))}
              />
            </label>
            <label className="form-field">
              <span className="field-label">Hasta</span>
              <input
                type="datetime-local"
                value={filtersDraft.to}
                onChange={(e) => setFiltersDraft((prev) => ({ ...prev, to: e.target.value }))}
              />
            </label>
            <label className="form-field">
              <span className="field-label">Usuario</span>
              <select
                value={filtersDraft.userId}
                onChange={(e) => setFiltersDraft((prev) => ({ ...prev, userId: e.target.value }))}
              >
                <option value="">Todos</option>
                {userOptions.map((u) => (
                  <option key={`events-user-${u.id}`} value={String(u.id)}>{userFilterLabel(u)}</option>
                ))}
              </select>
            </label>
            <label className="form-field">
              <span className="field-label">Vista</span>
              <select
                value={filtersDraft.ceeOnly ? 'cee' : 'all'}
                onChange={(e) => setFiltersDraft((prev) => ({ ...prev, ceeOnly: e.target.value === 'cee' }))}
              >
                <option value="all">Todos</option>
                <option value="cee">Solo CEE</option>
              </select>
            </label>
            <label className="form-field">
              <span className="field-label">Tipo de evento</span>
              <input
                type="text"
                placeholder="Ej. auth.login.success"
                value={filtersDraft.eventType}
                onChange={(e) => setFiltersDraft((prev) => ({ ...prev, eventType: e.target.value }))}
              />
            </label>
          </div>
          <div className="admin-toolbar-actions">
            <button className="btn-ghost" onClick={() => reset()}>
              Limpiar
            </button>
            <button className="btn-primary" onClick={() => applyFilters()}>
              {loadingSummary || loadingEvents ? <span className="spinner" /> : 'Aplicar filtros'}
            </button>
          </div>
        </section>

        <section className="admin-summary-grid">
          <SummaryCard label="Usuarios" value={summary?.users?.total ?? '-'} />
          <SummaryCard label="Usuarios activos" value={summary?.users?.active ?? '-'} />
          <SummaryCard label="Administradores" value={summary?.users?.admins ?? '-'} tone="accent" />
          <SummaryCard label="Asambleas" value={summary?.assemblies?.total ?? '-'} />
          <SummaryCard label="Asambleas completadas" value={summary?.assemblies?.completed ?? '-'} tone="success" />
          <SummaryCard label="Asambleas canceladas" value={summary?.assemblies?.canceled ?? '-'} tone="danger" />
          <SummaryCard label="Informes enviados" value={summary?.reports?.submitted ?? '-'} />
          <SummaryCard label="Asistentes" value={summary?.reports?.attendeesTotal ?? '-'} />
          <SummaryCard
            label="Logins exitosos (7 dias)"
            value={summary?.activity?.loginSuccessLast7Days ?? '-'}
          />
          <SummaryCard label="Eventos en rango" value={summary?.activity?.eventsInRange ?? '-'} />
        </section>

        <section className="admin-subsection-switch">
          <button
            className={`admin-subsection-btn ${adminSection === 'assemblies_info' ? 'active' : ''}`}
            onClick={() => setAdminSection('assemblies_info')}
          >
            Asambleas Registradas
          </button>
          <button
            className={`admin-subsection-btn ${adminSection === 'evidences_gallery' ? 'active' : ''}`}
            onClick={() => setAdminSection('evidences_gallery')}
          >
            Galeria de Evidencias
          </button>
          <button
            className={`admin-subsection-btn ${adminSection === 'users_actions' ? 'active' : ''}`}
            onClick={() => setAdminSection('users_actions')}
          >
            Usuarios y Acciones
          </button>
        </section>

        {adminSection === 'users_actions' && (
          <>
        <section className="admin-events-panel">
          <div className="admin-events-header">
            <h2>Eventos de actividad</h2>
            <div className="admin-events-head-right">
              <span>{loadingEvents ? 'Cargando...' : `${eventsResult.total || 0} evento(s)`}</span>
              <label className="admin-page-size">
                <span>Orden</span>
                <select
                  value={`${eventsSort.sortBy}:${eventsSort.sortOrder}`}
                  onChange={(e) => {
                    const [sortBy, sortOrder] = String(e.target.value).split(':');
                    setEventsOrder(sortBy, sortOrder);
                  }}
                  disabled={loadingEvents}
                >
                  <option value="createdAt:desc">Mas recientes</option>
                  <option value="createdAt:asc">Mas antiguos</option>
                  <option value="eventType:asc">Evento A-Z</option>
                  <option value="eventType:desc">Evento Z-A</option>
                </select>
              </label>
              <label className="admin-page-size">
                <span>Por pagina</span>
                <select
                  value={eventsResult.pageSize || 25}
                  onChange={(e) => changeEventsPageSize(Number(e.target.value))}
                  disabled={loadingEvents}
                >
                  <option value="25">25</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </label>
            </div>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Evento</th>
                  <th>Usuario</th>
                  <th>Actor</th>
                  <th>Entidad</th>
                  <th>Detalle</th>
                </tr>
              </thead>
              <tbody>
                {(eventsResult.items || []).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="admin-empty-cell">
                      No hay eventos para los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  (eventsResult.items || []).map((event) => (
                    <tr key={event.id}>
                      <td>{formatDateTime(event.createdAt)}</td>
                      <td>{event.eventType}</td>
                      <td>{event.userEmail || event.userId || '-'}</td>
                      <td>{event.actorUserEmail || event.actorUserId || '-'}</td>
                      <td>
                        {event.entityType ? `${event.entityType} #${event.entityId || '-'}` : '-'}
                      </td>
                      <td className="admin-metadata">{formatMetadata(event.metadata)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="admin-pagination">
            <button
              className="btn-ghost"
              onClick={() => changePage(page - 1)}
              disabled={loadingEvents || page <= 1}
            >
              Anterior
            </button>
            <span>
              Pagina {page} de {totalPages}
            </span>
            <button
              className="btn-ghost"
              onClick={() => changePage(page + 1)}
              disabled={loadingEvents || page >= totalPages}
            >
              Siguiente
            </button>
          </div>
        </section>

        <section className="admin-events-panel">
          <div className="admin-events-header">
            <h2>Usuarios registrados</h2>
            <div className="admin-events-head-right">
              <span>{loadingUsers ? 'Cargando...' : `${usersResult.total || 0} usuario(s)`}</span>
              <label className="admin-page-size">
                <span>Por pagina</span>
                <select
                  value={usersResult.pageSize || 20}
                  onChange={(e) => changeUsersPageSize(Number(e.target.value))}
                  disabled={loadingUsers}
                >
                  <option value="20">20</option>
                  <option value="50">50</option>
                  <option value="100">100</option>
                </select>
              </label>
            </div>
          </div>

          <div className="admin-users-filters">
            <label className="form-field">
              <span className="field-label">Buscar</span>
              <input
                type="text"
                placeholder="Nombre, correo, INE o CURP"
                value={usersFiltersDraft.search}
                onChange={(e) =>
                  setUsersFiltersDraft((prev) => ({ ...prev, search: e.target.value }))
                }
              />
            </label>
            <label className="form-field">
              <span className="field-label">Estatus</span>
              <select
                value={usersFiltersDraft.status}
                onChange={(e) =>
                  setUsersFiltersDraft((prev) => ({ ...prev, status: e.target.value }))
                }
              >
                <option value="">Todos</option>
                <option value="active">Activo</option>
                <option value="suspended">Suspendido</option>
              </select>
            </label>
            <label className="form-field">
              <span className="field-label">Tipo</span>
              <select
                value={usersFiltersDraft.isAdmin}
                onChange={(e) =>
                  setUsersFiltersDraft((prev) => ({ ...prev, isAdmin: e.target.value }))
                }
              >
                <option value="">Todos</option>
                <option value="true">Administradores</option>
                <option value="false">No administradores</option>
              </select>
            </label>
            <label className="form-field">
              <span className="field-label">Orden</span>
              <select
                value={`${usersFiltersDraft.sortBy}:${usersFiltersDraft.sortOrder}`}
                onChange={(e) => {
                  const [sortBy, sortOrder] = String(e.target.value).split(':');
                  setUsersSort(sortBy, sortOrder);
                }}
              >
                <option value="createdAt:desc">Alta reciente</option>
                <option value="createdAt:asc">Alta antigua</option>
                <option value="firstName:asc">Nombre A-Z</option>
                <option value="email:asc">Correo A-Z</option>
                <option value="status:asc">Estatus</option>
                <option value="isAdmin:desc">Admins primero</option>
              </select>
            </label>
            <div className="admin-users-filters-actions">
              <button className="btn-primary" onClick={() => applyUsersFilters()}>
                {loadingUsers ? <span className="spinner" /> : 'Buscar usuarios'}
              </button>
            </div>
          </div>

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nombre</th>
                  <th>Correo</th>
                  <th>INE</th>
                  <th>Tipo</th>
                  <th>Estatus</th>
                  <th>Alta</th>
                </tr>
              </thead>
              <tbody>
                {(usersResult.items || []).length === 0 ? (
                  <tr>
                    <td colSpan={7} className="admin-empty-cell">
                      No hay usuarios para los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  (usersResult.items || []).map((item) => (
                    <tr key={item.id}>
                      <td>{item.id}</td>
                      <td>
                        {item.firstName} {item.lastNamePaternal} {item.lastNameMaternal}
                      </td>
                      <td>{item.email}</td>
                      <td>{item.ineKey}</td>
                      <td>{item.isAdmin ? 'Administrador' : 'Usuario'}</td>
                      <td>{item.status}</td>
                      <td>{formatDateTime(item.createdAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="admin-pagination">
            <button
              className="btn-ghost"
              onClick={() => changeUsersPage(usersPage - 1)}
              disabled={loadingUsers || usersPage <= 1}
            >
              Anterior
            </button>
            <span>
              Pagina {usersPage} de {usersTotalPages}
            </span>
            <button
              className="btn-ghost"
              onClick={() => changeUsersPage(usersPage + 1)}
              disabled={loadingUsers || usersPage >= usersTotalPages}
            >
              Siguiente
            </button>
          </div>
        </section>
          </>
        )}

        {adminSection === 'assemblies_info' && (
          <section className="admin-events-panel">
            <div className="admin-events-header">
              <h2>Asambleas registradas por usuario</h2>
              <div className="admin-events-head-right">
                <span>
                  {loadingAssemblies
                    ? 'Cargando...'
                    : `${assembliesResult.total || 0} asamblea(s)`}
                </span>
                <label className="admin-page-size">
                  <span>Por pagina</span>
                  <select
                    value={assembliesResult.pageSize || 20}
                    onChange={(e) => changeAssembliesPageSize(Number(e.target.value))}
                    disabled={loadingAssemblies}
                  >
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="admin-assemblies-filters">
              <label className="form-field">
                <span className="field-label">Buscar</span>
                <input
                  type="text"
                  placeholder="Usuario, correo, INE, colonia o direccion"
                  value={assembliesFiltersDraft.search}
                  onChange={(e) =>
                    setAssembliesFiltersDraft((prev) => ({ ...prev, search: e.target.value }))
                  }
                />
              </label>
              <label className="form-field">
                <span className="field-label">Usuario</span>
                <select
                  value={assembliesFiltersDraft.userId}
                  onChange={(e) =>
                    setAssembliesFiltersDraft((prev) => ({ ...prev, userId: e.target.value }))
                  }
                >
                  <option value="">Todos</option>
                  {userOptions.map((u) => (
                    <option key={`assemblies-user-${u.id}`} value={String(u.id)}>{userFilterLabel(u)}</option>
                  ))}
                </select>
              </label>
              <label className="form-field">
                <span className="field-label">Estado asamblea</span>
                <select
                  value={assembliesFiltersDraft.status}
                  onChange={(e) =>
                    setAssembliesFiltersDraft((prev) => ({ ...prev, status: e.target.value }))
                  }
                >
                  <option value="">Todos</option>
                  <option value="pending">Pendiente</option>
                  <option value="in_progress">En progreso</option>
                  <option value="completed">Completada</option>
                  <option value="canceled">Cancelada</option>
                </select>
              </label>
              <label className="form-field">
                <span className="field-label">Estado reporte</span>
                <select
                  value={assembliesFiltersDraft.reportStatus}
                  onChange={(e) =>
                    setAssembliesFiltersDraft((prev) => ({ ...prev, reportStatus: e.target.value }))
                  }
                >
                  <option value="">Todos</option>
                  <option value="draft">Borrador</option>
                  <option value="submitted">Enviado</option>
                </select>
              </label>
              <label className="form-field">
                <span className="field-label">Desde</span>
                <input
                  type="datetime-local"
                  value={assembliesFiltersDraft.from}
                  onChange={(e) =>
                    setAssembliesFiltersDraft((prev) => ({ ...prev, from: e.target.value }))
                  }
                />
              </label>
              <label className="form-field">
                <span className="field-label">Hasta</span>
                <input
                  type="datetime-local"
                  value={assembliesFiltersDraft.to}
                  onChange={(e) =>
                    setAssembliesFiltersDraft((prev) => ({ ...prev, to: e.target.value }))
                  }
                />
              </label>
              <label className="form-field">
                <span className="field-label">Orden</span>
                <select
                  value={`${assembliesFiltersDraft.sortBy}:${assembliesFiltersDraft.sortOrder}`}
                  onChange={(e) => {
                    const [sortBy, sortOrder] = String(e.target.value).split(':');
                    setAssembliesSort(sortBy, sortOrder);
                  }}
                >
                  <option value="scheduledDate:desc">Fecha programada reciente</option>
                  <option value="scheduledDate:asc">Fecha programada antigua</option>
                  <option value="createdAt:desc">Alta reciente</option>
                  <option value="userName:asc">Usuario A-Z</option>
                  <option value="status:asc">Estado</option>
                </select>
              </label>
              <div className="admin-users-filters-actions">
                <button className="btn-primary" onClick={() => applyAssembliesFilters()}>
                  {loadingAssemblies ? <span className="spinner" /> : 'Buscar asambleas'}
                </button>
                <button
                  className="btn-ghost"
                  onClick={exportAssembliesByUser}
                  disabled={loadingAssembliesByUser || (assembliesByUserResult.items || []).length === 0}
                  title="Exportar tabla de asambleas por usuario"
                >
                  Exportar Excel
                </button>
                <button
                  className="btn-ghost"
                  onClick={exportAssembliesDetail}
                  disabled={loadingAssemblies || (assembliesResult.items || []).length === 0}
                  title="Exportar tabla de detalle de asambleas"
                >
                  Exportar detalle
                </button>
              </div>
            </div>

            <div className="admin-table-wrap" style={{ marginBottom: '14px' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>CEE / entidad</th>
                    <th>Total</th>
                    <th>Pendiente</th>
                    <th>En progreso</th>
                    <th>Completada</th>
                    <th>Cancelada</th>
                    <th>Asistentes</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingAssembliesByUser ? (
                    <tr>
                      <td colSpan={8} className="admin-empty-cell">Cargando reporte por usuario...</td>
                    </tr>
                  ) : (assembliesByUserResult.items || []).length === 0 ? (
                    <tr>
                      <td colSpan={8} className="admin-empty-cell">
                        No hay datos para el reporte por usuario.
                      </td>
                    </tr>
                  ) : (
                    (assembliesByUserResult.items || []).map((item) => (
                      <tr key={`report-user-${item.userId}`}>
                        <td>
                          <strong>{item.userFullName || '-'}</strong>
                          <br />
                          <span className="admin-inline-muted">{item.userEmail}</span>
                        </td>
                        <td>
                          {item.isCee ? (
                            <>
                              <span>Sí</span>
                              <br />
                              <span className="admin-inline-muted">{item.ceeName || 'CEE sin nombre'}</span>
                            </>
                          ) : (
                            'No'
                          )}
                        </td>
                        <td>{item.total}</td>
                        <td>{item.pending}</td>
                        <td>{item.inProgress}</td>
                        <td>{item.completed}</td>
                        <td>{item.canceled}</td>
                        <td>{item.attendeesTotal ?? 0}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Usuario</th>
                    <th>Fecha</th>
                    <th>Horario</th>
                    <th>Ubicacion</th>
                    <th>Estado</th>
                    <th>Asistentes</th>
                    <th>Reporte</th>
                  </tr>
                </thead>
                <tbody>
                  {(assembliesResult.items || []).length === 0 ? (
                    <tr>
                      <td colSpan={7} className="admin-empty-cell">
                        No hay asambleas para los filtros seleccionados.
                      </td>
                    </tr>
                  ) : (
                    (assembliesResult.items || []).map((item) => (
                      <tr key={item.id}>
                        <td>{item.id}</td>
                        <td>
                          <strong>{item.userFullName || '-'}</strong>
                          <br />
                          <span className="admin-inline-muted">{item.userEmail}</span>
                        </td>
                        <td>{item.scheduledDate}</td>
                        <td>
                          {item.startTime} - {item.endTime}
                        </td>
                        <td>
                          {item.colonia}, CP {item.postalCode}
                          <br />
                          <span className="admin-inline-muted">{item.address}</span>
                        </td>
                        <td>{assemblyStatusLabel(item.status)}</td>
                        <td>{item.attendeesCount != null ? item.attendeesCount : '-'}</td>
                        <td>
                          {item.reportStatus ? (
                            <>
                              {reportStatusLabel(item.reportStatus)}
                              {item.reportSubmittedAt ? (
                                <>
                                  <br />
                                  <span className="admin-inline-muted">
                                    {formatDateTime(item.reportSubmittedAt)}
                                  </span>
                                </>
                              ) : null}
                            </>
                          ) : (
                            '-'
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="admin-pagination">
              <button
                className="btn-ghost"
                onClick={() => changeAssembliesPage(assembliesPage - 1)}
                disabled={loadingAssemblies || assembliesPage <= 1}
              >
                Anterior
              </button>
              <span>
                Pagina {assembliesPage} de {assembliesTotalPages}
              </span>
              <button
                className="btn-ghost"
                onClick={() => changeAssembliesPage(assembliesPage + 1)}
                disabled={loadingAssemblies || assembliesPage >= assembliesTotalPages}
              >
                Siguiente
              </button>
            </div>
          </section>
        )}

        {adminSection === 'evidences_gallery' && (
          <section className="admin-events-panel">
            <div className="admin-events-header">
              <h2>Galeria de evidencias</h2>
              <div className="admin-events-head-right">
                <span>
                  {loadingEvidences
                    ? 'Cargando...'
                    : `${evidencesResult.total || 0} evidencia(s)`}
                </span>
                <label className="admin-page-size">
                  <span>Por pagina</span>
                  <select
                    value={evidencesResult.pageSize || 24}
                    onChange={(e) => changeEvidencesPageSize(Number(e.target.value))}
                    disabled={loadingEvidences}
                  >
                    <option value="12">12</option>
                    <option value="24">24</option>
                    <option value="48">48</option>
                    <option value="96">96</option>
                  </select>
                </label>
              </div>
            </div>

            <div className="admin-assemblies-filters">
              <label className="form-field">
                <span className="field-label">Buscar</span>
                <input
                  type="text"
                  placeholder="Archivo, usuario o domicilio"
                  value={evidencesFiltersDraft.search}
                  onChange={(e) =>
                    setEvidencesFiltersDraft((prev) => ({ ...prev, search: e.target.value }))
                  }
                />
              </label>
              <label className="form-field">
                <span className="field-label">Usuario</span>
                <select
                  value={evidencesFiltersDraft.userId}
                  onChange={(e) =>
                    setEvidencesFiltersDraft((prev) => ({ ...prev, userId: e.target.value }))
                  }
                >
                  <option value="">Todos</option>
                  {userOptions.map((u) => (
                    <option key={`evidences-user-${u.id}`} value={String(u.id)}>{userFilterLabel(u)}</option>
                  ))}
                </select>
              </label>
              <label className="form-field">
                <span className="field-label">Tipo</span>
                <select
                  value={evidencesFiltersDraft.fileType}
                  onChange={(e) =>
                    setEvidencesFiltersDraft((prev) => ({ ...prev, fileType: e.target.value }))
                  }
                >
                  <option value="">Todos</option>
                  <option value="photo">Fotografia</option>
                  <option value="document">Documento</option>
                </select>
              </label>
              <label className="form-field">
                <span className="field-label">Desde</span>
                <input
                  type="datetime-local"
                  value={evidencesFiltersDraft.from}
                  onChange={(e) =>
                    setEvidencesFiltersDraft((prev) => ({ ...prev, from: e.target.value }))
                  }
                />
              </label>
              <label className="form-field">
                <span className="field-label">Hasta</span>
                <input
                  type="datetime-local"
                  value={evidencesFiltersDraft.to}
                  onChange={(e) =>
                    setEvidencesFiltersDraft((prev) => ({ ...prev, to: e.target.value }))
                  }
                />
              </label>
              <label className="form-field">
                <span className="field-label">Orden</span>
                <select
                  value={`${evidencesFiltersDraft.sortBy}:${evidencesFiltersDraft.sortOrder}`}
                  onChange={(e) => {
                    const [sortBy, sortOrder] = String(e.target.value).split(':');
                    setEvidencesSort(sortBy, sortOrder);
                  }}
                >
                  <option value="createdAt:desc">Carga reciente</option>
                  <option value="createdAt:asc">Carga antigua</option>
                  <option value="scheduledDate:desc">Asamblea reciente</option>
                  <option value="scheduledDate:asc">Asamblea antigua</option>
                  <option value="userName:asc">Usuario A-Z</option>
                </select>
              </label>
              <div className="admin-users-filters-actions">
                <button className="btn-primary" onClick={() => applyEvidencesFilters()}>
                  {loadingEvidences ? <span className="spinner" /> : 'Buscar evidencias'}
                </button>
              </div>
            </div>

            <div className="evidence-gallery-grid">
              {(evidencesResult.items || []).length === 0 && (
                <p className="admin-empty-cell">No hay evidencias para los filtros seleccionados.</p>
              )}
              {(evidencesResult.items || []).map((item) => (
                <article className="evidence-card" key={item.id}>
                  <button
                    className="evidence-preview"
                    onClick={() => setSelectedEvidence(item)}
                    title="Abrir vista"
                  >
                    {item.fileType === 'photo' ? (
                      <img src={withAuthToken(item.fileUrl)} alt={item.originalName} loading="lazy" />
                    ) : (
                      <div className="evidence-doc-preview">📄</div>
                    )}
                  </button>
                  <div className="evidence-card-body">
                    <strong className="evidence-card-title">{evidenceTitle(item)}</strong>
                    <span className="admin-inline-muted">{item.originalName}</span>
                    <span className="admin-inline-muted">{item.userFullName || item.userEmail}</span>
                    <span className="admin-inline-muted">{formatDateTime(item.createdAt)}</span>
                    <span className="admin-inline-muted">{formatFileSize(item.fileSizeBytes)}</span>
                    <div className="evidence-card-actions">
                      <a href={withAuthToken(item.fileUrl)} target="_blank" rel="noreferrer">Abrir</a>
                      <a href={withAuthToken(item.fileUrl)} download={item.originalName}>Descargar</a>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            <div className="admin-pagination">
              <button
                className="btn-ghost"
                onClick={() => changeEvidencesPage(evidencesPage - 1)}
                disabled={loadingEvidences || evidencesPage <= 1}
              >
                Anterior
              </button>
              <span>
                Pagina {evidencesPage} de {evidencesTotalPages}
              </span>
              <button
                className="btn-ghost"
                onClick={() => changeEvidencesPage(evidencesPage + 1)}
                disabled={loadingEvidences || evidencesPage >= evidencesTotalPages}
              >
                Siguiente
              </button>
            </div>
          </section>
        )}
          </>
        )}

        {activeModule === 'newspaper' && <NewspaperRequestsPanel adminCtx={adminCtx} />}
      </div>
      </div>

      {selectedEvidence && (
        <div className="modal-overlay" onClick={() => setSelectedEvidence(null)}>
          <div className="modal-card evidence-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{evidenceTitle(selectedEvidence)}</h3>
              <button className="btn-icon" onClick={() => setSelectedEvidence(null)}>✕</button>
            </div>
            <div className="evidence-modal-body">
              {selectedEvidence.fileType === 'photo' ? (
                <img src={withAuthToken(selectedEvidence.fileUrl)} alt={selectedEvidence.originalName} className="evidence-modal-image" />
              ) : (
                <iframe
                  src={withAuthToken(selectedEvidence.fileUrl)}
                  title={selectedEvidence.originalName}
                  className="evidence-modal-doc"
                />
              )}
            </div>
            <div className="modal-footer">
              <a className="btn-ghost" href={withAuthToken(selectedEvidence.fileUrl)} target="_blank" rel="noreferrer">
                Abrir en pestaña nueva
              </a>
              <a className="btn-primary" href={withAuthToken(selectedEvidence.fileUrl)} download={selectedEvidence.originalName}>
                Descargar
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
